import type { AuthTokens, Session, User } from "../model/types";

const TOKEN_KEYS: (keyof AuthTokens)[] = [
  "access-token",
  "token-type",
  "client",
  "expiry",
  "uid",
  "authorization",
];

const USER_INFO_KEYS: (keyof User)[] = [
  "name",
  "role",
  "email",
  "id",
  "companyId",
  "companyName",
];

// アプリケーション固有のプレフィックス
const AUTH_KEY_PREFIX = "freight-matching-auth:";

// LocalStorage操作のヘルパー関数（エラーハンドリング付き）
const getLocalStorageItem = (key: string): string | null => {
  try {
    return localStorage.getItem(`${AUTH_KEY_PREFIX}${key}`);
  } catch (error) {
    console.error(
      `LocalStorageからの値取得に失敗しました。キー: ${key}`,
      error,
    );
    return null;
  }
};

const setLocalStorageItem = (key: string, value: string): void => {
  try {
    localStorage.setItem(`${AUTH_KEY_PREFIX}${key}`, value);
  } catch (error) {
    console.error(
      `LocalStorageへの値保存に失敗しました。キー: ${key}, 値: ${value}`,
      error,
    );
    throw new Error(
      "認証情報の保存に失敗しました。ブラウザの設定を確認してください。",
    );
  }
};

const removeLocalStorageItem = (key: string): void => {
  try {
    localStorage.removeItem(`${AUTH_KEY_PREFIX}${key}`);
  } catch (error) {
    console.error(
      `LocalStorageからの値削除に失敗しました。キー: ${key}`,
      error,
    );
  }
};

export const getStoredSession = (): Session | null => {
  try {
    const tokens = TOKEN_KEYS.reduce(
      (acc, key) => {
        const value = getLocalStorageItem(key);
        if (value !== null) acc[key] = value;
        return acc;
      },
      {} as Partial<AuthTokens>,
    );

    const userInfo = USER_INFO_KEYS.reduce(
      (acc, key) => {
        const value = getLocalStorageItem(key);
        if (value !== null) {
          if (key === "id" || key === "companyId") {
            const numValue = Number.parseInt(value);
            if (Number.isNaN(numValue)) {
              console.error(
                `ユーザー情報の数値変換に失敗しました。キー: ${key}, 値: ${value}`,
              );
              return acc;
            }
            acc[key] = numValue;
          } else {
            acc[key] = value;
          }
        }
        return acc;
      },
      {} as Partial<User>,
    );

    // 必要なトークン情報がすべて揃っているかチェック
    const missingTokens = TOKEN_KEYS.filter((key) => !(key in tokens));
    if (missingTokens.length > 0) {
      console.warn(
        `認証トークンが不完全です。不足しているキー: ${missingTokens.join(", ")}`,
      );
      return null;
    }

    // 必要なユーザー情報がすべて揃っているかチェック
    const missingUserInfo = USER_INFO_KEYS.filter((key) => !(key in userInfo));
    if (missingUserInfo.length > 0) {
      console.warn(
        `ユーザー情報が不完全です。不足しているキー: ${missingUserInfo.join(", ")}`,
      );
      return null;
    }

    // トークンの期限チェック
    if (tokens.expiry) {
      const expiryTime = Number.parseInt(tokens.expiry) * 1000; // Unix timestamp (seconds) to milliseconds
      const currentTime = Date.now();
      if (currentTime >= expiryTime) {
        console.warn("認証トークンの有効期限が切れています");
        // 期限切れのトークンを削除
        removeStoredSession();
        return null;
      }
    }

    return {
      tokens: tokens as AuthTokens,
      user: userInfo as User,
    };
  } catch (error) {
    console.error("保存された認証情報の取得中にエラーが発生しました:", error);
    return null;
  }
};

export const setStoredSession = (session: Session): void => {
  try {
    // トークン情報を保存
    for (const key of TOKEN_KEYS) {
      const value = session.tokens[key];
      if (value !== undefined && value !== null) {
        setLocalStorageItem(key, value);
      } else {
        console.warn(`認証トークンが空です。キー: ${key}`);
      }
    }

    // ユーザー情報を保存
    for (const key of USER_INFO_KEYS) {
      const value = session.user[key];
      if (value !== undefined && value !== null) {
        setLocalStorageItem(key, value.toString());
      } else {
        console.warn(`ユーザー情報が空です。キー: ${key}`);
      }
    }

    console.log("認証情報がローカルストレージに正常に保存されました");
  } catch (error) {
    console.error("認証情報の保存中にエラーが発生しました:", error);
    throw new Error(
      "認証情報の保存に失敗しました。再度ログインを試してください。",
    );
  }
};

export const removeStoredSession = (): void => {
  try {
    const allKeys = [...TOKEN_KEYS, ...USER_INFO_KEYS];
    allKeys.forEach(removeLocalStorageItem);
    console.log("認証情報がローカルストレージから正常に削除されました");
  } catch (error) {
    console.error("認証情報の削除中にエラーが発生しました:", error);
    // 削除時のエラーは致命的ではないため、例外は投げない
  }
};
